import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Paperclip,
  Image,
  User,
  Headphones,
  Loader2,
  Minimize2,
  Maximize2,
  XCircle,
} from "lucide-react";

const ChatWidget = () => {
  const { user, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ name: "", email: "" });
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Hide chat widget on admin page
  const isAdminPage = window.location.pathname.startsWith("/admin");

  // Get or create guest ID
  const getGuestId = () => {
    let guestId = localStorage.getItem("chat_guest_id");
    if (!guestId) {
      guestId = `guest_${Math.random().toString(36).substr(2, 12)}`;
      localStorage.setItem("chat_guest_id", guestId);
    }
    return guestId;
  };

  // Start or get chat session
  const startChat = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const payload = isAuthenticated ? {} : {
        guest_id: getGuestId(),
        guest_name: guestInfo.name || "Guest",
        guest_email: guestInfo.email || null
      };

      const res = await axios.post(`${API}/chat/start`, payload, {
        headers,
        withCredentials: true
      });
      
      setChatSession(res.data);
      await fetchMessages(res.data.chat_id);
      setShowGuestForm(false);
    } catch (error) {
      console.error("Failed to start chat:", error);
      toast.error("Failed to connect to support");
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for current chat
  const fetchMessages = async (chatId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/chat/${chatId}/messages`, {
        headers,
        withCredentials: true
      });
      
      setMessages(res.data || []);
      
      // Check if last message indicates chat was closed by admin
      const lastMsg = res.data?.[res.data.length - 1];
      if (lastMsg?.sender_type === "system" && lastMsg?.content?.includes("closed")) {
        setChatSession(prev => prev ? { ...prev, status: "closed" } : prev);
      }
      
      scrollToBottom();
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  // Check chat session status (detects admin closing)
  const checkChatStatus = async (chatId) => {
    try {
      const guestId = localStorage.getItem("chat_guest_id");
      if (guestId) {
        const res = await axios.get(`${API}/chat/guest/${guestId}`);
        if (res.data?.status === "closed") {
          setChatSession(prev => prev ? { ...prev, status: "closed" } : prev);
        }
      }
    } catch {}
  };

  // Send message
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !chatSession) return;

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(
        `${API}/chat/${chatSession.chat_id}/message`,
        {
          content: newMessage,
          guest_name: guestInfo.name || chatSession.user_name
        },
        { headers, withCredentials: true }
      );
      
      setMessages(prev => [...prev, res.data]);
      setNewMessage("");
      scrollToBottom();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !chatSession) return;

    // For now, we'll create a data URL (in production, upload to cloud storage)
    const reader = new FileReader();
    reader.onload = async (event) => {
      const isImage = file.type.startsWith("image/");
      
      setSending(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const res = await axios.post(
          `${API}/chat/${chatSession.chat_id}/message`,
          {
            content: `Sent ${isImage ? "an image" : "a file"}: ${file.name}`,
            attachment_url: event.target.result,
            attachment_type: isImage ? "image" : "file",
            guest_name: guestInfo.name || chatSession.user_name
          },
          { headers, withCredentials: true }
        );
        
        setMessages(prev => [...prev, res.data]);
        scrollToBottom();
      } catch (error) {
        toast.error("Failed to send attachment");
      } finally {
        setSending(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleEndChat = async () => {
    if (!chatSession) return;
    try {
      await axios.put(`${API}/chat/${chatSession.chat_id}/end`);
      setChatSession(prev => ({ ...prev, status: "closed" }));
      toast.success("Chat ended");
    } catch (error) {
      toast.error("Failed to end chat");
    }
  };

  const handleStartNewChat = () => {
    setChatSession(null);
    setMessages([]);
    setNewMessage("");
    if (!isAuthenticated) {
      setShowGuestForm(true);
    }
  };

  // Poll for new messages and status
  useEffect(() => {
    if (!chatSession || !isOpen) return;
    
    const interval = setInterval(() => {
      fetchMessages(chatSession.chat_id);
      if (chatSession.status !== "closed") {
        checkChatStatus(chatSession.chat_id);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [chatSession, isOpen]);

  // Check for existing guest chat on mount
  useEffect(() => {
    if (!isAuthenticated) {
      const guestId = getGuestId();
      axios.get(`${API}/chat/guest/${guestId}`).then(res => {
        if (res.data) {
          setChatSession(res.data);
        }
      }).catch(() => {});
    }
  }, [isAuthenticated]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    
    if (chatSession) {
      fetchMessages(chatSession.chat_id);
    } else if (!isAuthenticated) {
      setShowGuestForm(true);
    } else {
      startChat();
    }
  };

  const handleGuestStart = (e) => {
    e.preventDefault();
    if (!guestInfo.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    startChat();
  };

  if (isAdminPage) return null;

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full shadow-lg shadow-amber-500/30 flex items-center justify-center text-white hover:scale-110 transition-transform"
          data-testid="chat-widget-button"
        >
          <MessageCircle className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 rounded-full text-xs flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden transition-all duration-300 ${
            isMinimized ? "w-72 h-14" : "w-96 h-[500px]"
          }`}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-navy-600 to-navy-700 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Support Chat</h3>
                {!isMinimized && (
                  <p className="text-white/70 text-xs">We typically reply in minutes</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {chatSession && chatSession.status !== "closed" && (
                <button
                  onClick={handleEndChat}
                  className="text-white/70 hover:text-rose-300 p-1"
                  title="End chat"
                  data-testid="end-chat-btn"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="text-white/70 hover:text-white p-1"
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Guest Form */}
              {showGuestForm && !chatSession && (
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    Please enter your details to start chatting with our support team.
                  </p>
                  <form onSubmit={handleGuestStart} className="space-y-4">
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">Your Name *</label>
                      <Input
                        placeholder="Enter your name"
                        value={guestInfo.name}
                        onChange={(e) => setGuestInfo({ ...guestInfo, name: e.target.value })}
                        className="border-slate-300"
                        data-testid="guest-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-slate-600 mb-1 block">Email (optional)</label>
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={guestInfo.email}
                        onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                        className="border-slate-300"
                        data-testid="guest-email-input"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-gold" disabled={loading}>
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Start Chat"}
                    </Button>
                  </form>
                </div>
              )}

              {/* Loading */}
              {loading && !showGuestForm && (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
                </div>
              )}

              {/* Messages */}
              {chatSession && !loading && (
                <>
                  <div className="flex-1 h-[340px] overflow-y-auto p-4 space-y-4 bg-slate-50">
                    {messages.length === 0 && (
                      <div className="text-center py-8">
                        <Headphones className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Start a conversation</p>
                        <p className="text-slate-400 text-sm">Our team is here to help</p>
                      </div>
                    )}
                    
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_type === "admin" ? "justify-start" : "justify-end"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl p-3 ${
                            msg.sender_type === "admin"
                              ? "bg-white border border-slate-200 rounded-bl-md"
                              : "bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-br-md"
                          }`}
                        >
                          {msg.sender_type === "admin" && (
                            <p className="text-xs text-navy-600 font-medium mb-1 flex items-center gap-1">
                              <Headphones className="w-3 h-3" /> {msg.sender_name || "Support"}
                            </p>
                          )}
                          
                          {msg.attachment_url && msg.attachment_type === "image" && (
                            <img
                              src={msg.attachment_url}
                              alt="Attachment"
                              className="max-w-full rounded-lg mb-2"
                            />
                          )}
                          
                          <p className={`text-sm ${msg.sender_type === "admin" ? "text-slate-700" : ""}`}>
                            {msg.content}
                          </p>
                          
                          <p className={`text-xs mt-1 ${
                            msg.sender_type === "admin" ? "text-slate-400" : "text-white/70"
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  {chatSession?.status === "closed" ? (
                    <div className="p-4 bg-slate-50 border-t border-slate-200 text-center">
                      <p className="text-sm text-slate-500 mb-2">This chat has ended</p>
                      <Button
                        size="sm"
                        onClick={handleStartNewChat}
                        className="bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="start-new-chat-btn"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" /> Start New Chat
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-slate-200">
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="p-2 text-slate-400 hover:text-navy-600 transition-colors"
                          disabled={sending}
                        >
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="flex-1 border-slate-300"
                          disabled={sending}
                          data-testid="chat-message-input"
                        />
                        <Button
                          type="submit"
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600"
                          disabled={sending || !newMessage.trim()}
                          data-testid="chat-send-button"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
